/* eslint-disable no-unused-vars */
import moment from 'moment';
import { sortBy, find, map, union, flatten, chain, some } from 'lodash';

import { eventGenderTypeIds, drawEliminationTypeIds, drawPlacementStatusTypeIds, drawPositionTypeIds, drawCategoryTypeIds, matchCounts, registrationStatusTypeIds, eventStateTypeIds, divisionFormatTypeIds } from './eventConstants';
import { sortObjectsByProp, getRatingDetails, setRatingChoice, getPickleBallRating } from '.';
import { shouldDisplayRoundNumbersV2, getOrganizerRating, isDoublesFormat, isSinglesFormat } from './events';
import { comparator } from './comparator';
import { getComparatorArguments } from './sorting';
import { getLabelByTypeId } from './types';
import { gameTypes, sortParamTypes } from './constants';
import { OUTCOMES_WITH_NO_WINNER } from '../pages/DrawEditPage/EventDeskPostScore/consts';

const _ = { sortBy, find, map, union, flatten, chain, some };

// returns an array of draw id's the user is in
export const findUserDrawIds = (profilePlayerId, foundRegisteredSinglesPlayer, event) => {
    // populate all draws where the user is placed
    const foundDrawIds = [];

    // map the dictionary for draws as an array
    const userDraws = foundRegisteredSinglesPlayer.draws;
    const foundDrawsInArray = Object.values(userDraws);

    // find the user's draw that they are placed in (singles)
    if (foundDrawsInArray.length) {
        foundDrawsInArray.forEach(draw => {
            if (draw?.status?.value === 'placed') {
                foundDrawIds.push(draw.id);
            }
        });
        // find all teams the user is involved in, use that team as reference to find the respective draw id (doubles)
        event.teams.forEach(team => {
            if (team.players.includes(profilePlayerId)) {
                const foundDoublesDrawArray = Object.values(team.draws);
                foundDoublesDrawArray.forEach(draw => {
                    if (draw?.status?.value === 'placed') {
                        foundDrawIds.push(draw.id);
                    }
                });
            }
        });
    }
    return foundDrawIds;
};

export const findUserDrawIdsV2 = (player, teams) => {
    const foundDrawIds = [];
    player.draws.forEach(draw => {
        if (draw.drawPlacementStatusId === drawPlacementStatusTypeIds.PLACED) {
            foundDrawIds.push(draw.id);
        }
    });
    teams.data.forEach(team => {
        if (team.members.includes(player.memberId)) {
            team.draws.forEach(draw => {
                if (draw.drawPlacementStatusId === drawPlacementStatusTypeIds.PLACED) {
                    foundDrawIds.push(draw.id);
                }
            });
        }
    });
    return foundDrawIds;
};

// returns an array of the draw objects the user is placed in
export const findUserDraws = (draws, foundUserDrawIds) => {
    const allFoundDraws = [];
    // populate the draw objects into an array that can match to the found draw id's array
    draws.forEach(draw => {
        if (foundUserDrawIds.includes(draw.id) || draw.childDraws?.some(cd => foundUserDrawIds.includes(cd.id))) {
            allFoundDraws.push(draw);
        }
    });
    return allFoundDraws;
};

export const populateChildDrawOptions = (draw) => {
    let childDraws = [];
    if (draw.childDraws) {
        if (draw.drawCategoryType?.value === 'grouped') {
            childDraws = draw.childDraws.sort(sortObjectsByProp('name')).map((childDraw) => {
                return {
                    value: childDraw.id,
                    label: childDraw.name,
                };
            });
        } else {
            childDraws = draw.childDraws.map((childDraw) => {
                if (childDraw.id && childDraw.drawCategoryType.label) {
                    return {
                        value: childDraw.id,
                        label: childDraw.drawCategoryType.label,
                    };
                }
                return null;
            });
        }
    }
    return childDraws;
};

export const populateChildDrawOptionsV2 = (draw, drawCategoryTypes) => {
    let childDraws = [];
    if (draw.childDraws) {
        if (draw.drawCategoryTypeId === drawCategoryTypeIds.GROUPED) {
            childDraws = draw.childDraws.sort(sortObjectsByProp('name')).map((childDraw) => {
                return {
                    value: childDraw.id,
                    label: childDraw.name,
                };
            });
        } else {
            childDraws = draw.childDraws.map((childDraw) => {
                if (childDraw.id && childDraw.drawCategoryTypeId) {
                    return {
                        value: childDraw.id,
                        label: getLabelByTypeId(drawCategoryTypes, childDraw.drawCategoryTypeId),
                    };
                }
                return null;
            });
        }
    }
    return childDraws;
};

export function getAvailablePlayersForDraw(draw, selectedRound, players) {
    const round = draw.rounds[selectedRound];
    const playersInRoundWithResult = [];
    round.matches.map(m => {
        if (m.result) {
            if (m.position1) {
                playersInRoundWithResult.push(parseInt(m.position1.id, 10));
            }
            if (m.position2) {
                playersInRoundWithResult.push(parseInt(m.position2.id, 10));
            }
        }
        return false;
    });
    const isMatchPlay = draw?.eliminationType?.value === 'match_play';
    const available = players.filter(player => isMatchPlay || (draw.players.includes(player.id) && !playersInRoundWithResult.includes(player.id)));
    return _.sortBy(available, 'firstName');
}

export function getAvailableTeamsForDraw(draw, selectedRound, teams) {
    const round = draw.rounds[selectedRound];
    const teamsInRoundWithResult = [];
    round.matches.map(m => {
        if (m.result) {
            if (m.position1) {
                teamsInRoundWithResult.push(m.position1.id);
            }
            if (m.position2) {
                teamsInRoundWithResult.push(m.position2.id);
            }
        }
        return false;
    });
    const isMatchPlay = draw?.eliminationType?.value === 'match_play';
    return teams.filter(team => isMatchPlay || (draw.teams?.includes(team.id) && !teamsInRoundWithResult.includes(team.id)));
}

export function getAvailablePlayersForDrawV2(draw, selectedRound, players, membersInDraw) {
    const round = draw.rounds[selectedRound];
    const playersInRoundWithResult = [];
    if (round) {
        round.matches.forEach(m => {
            if (m.result) {
                if (m.position1) {
                    playersInRoundWithResult.push(parseInt(m.position1.memberId, 10));
                }
                if (m.position2) {
                    playersInRoundWithResult.push(parseInt(m.position2.memberId, 10));
                }
            }
        });
    }
    const isMatchPlay = draw?.eliminationTypeId === drawEliminationTypeIds.MATCH_PLAY;
    const available = players.filter(player => membersInDraw?.includes(player.memberId) && (isMatchPlay || !playersInRoundWithResult.includes(player.memberId)));
    return _.sortBy(available, 'firstName');
}

export function getAvailableTeamsForDrawV2(draw, selectedRound, teams, teamsInDraw, teamsDict) {
    const round = draw.rounds[selectedRound];
    // filter out teams with score (locked in match position)
    const teamsInRoundWithResult = [];
    if (round) {
        round.matches.forEach(m => {
            if (m.result) {
                if (m.position1) {
                    teamsInRoundWithResult.push(m.position1.teamId);
                }
                if (m.position2) {
                    teamsInRoundWithResult.push(m.position2.teamId);
                }
            }
        });
    }
    // filter out the other teams of players who already have score somewhere, rules:
    // Round robin & Ad-Hoc - players can only be on 1 team in a given round
    // Match play - players can be on multiple teams in a given round
    let teamPlayersInRoundWithResult = [];
    teamsInRoundWithResult.forEach(teamId => {
        teamPlayersInRoundWithResult = [...teamPlayersInRoundWithResult, ...teamsDict[teamId].members];
    });

    const isMatchPlay = draw?.eliminationTypeId === drawEliminationTypeIds.MATCH_PLAY;

    if (isMatchPlay) {
        return teams.filter(team => teamsInDraw?.includes(team.id));
    }

    return teams.filter(team => teamsInDraw?.includes(team.id) && !(teamsInRoundWithResult.includes(team.id) || teamPlayersInRoundWithResult.includes(team.members[0]) || teamPlayersInRoundWithResult.includes(team.members[1])));
}

export function canMoveTeamToDraw(drawId, teamId, draws, teams) {
    // search in child draws as well by flattening
    const draw = _.chain(draws)
        .thru(coll => {
            return _.union(coll, _.map(coll, 'childDraws') || []);
        })
        .flatten()
        .find({ id: drawId })
        .value();

    const isMatchPlay = draw?.eliminationType?.value === 'match_play';
    const teamPlayers = _.find(teams, { id: teamId });
    let canMovePlayer = true;

    if (!isMatchPlay) {
        draw.teams.forEach(drawTeamId => {
            const drawTeamPlayers = _.find(teams, { id: drawTeamId });
            teamPlayers.players.forEach(teamPlayerId => {
                if (drawTeamPlayers.players.indexOf(teamPlayerId) !== -1) {
                    canMovePlayer = false;
                }
            });
        });
    }

    return canMovePlayer;
}

export function playersNotInDoublesDraw(draw, players, teams, teamsDict) {
    // players already in the draw that are not in a team
    const playersInDraw = [...draw.players];
    // players that are in teams
    draw.teams.forEach(teamId => {
        if (teamsDict[teamId]?.players[0]) {
            playersInDraw.push(teamsDict[teamId].players[0]);
        }
        if (teamsDict[teamId]?.players[1]) {
            playersInDraw.push(teamsDict[teamId].players[1]);
        }
    });
    return players.filter(player => !playersInDraw.includes(player.id));
}

export function arePlayersInTeam(players, teams) {
    return _.some(teams, (team) => (team.players.includes(players[0]) && team.players.includes(players[1])));
}
export function arePlayersInTeamV2(players, teams) {
    return teams.length && _.some(teams, (team) => (team.members.includes(players[0]) && team.members.includes(players[1])));
}

export const getPlayerHSGradStatus = (player = {}) => {
    const { classYear, gradYearHs, highSchool } = player;

    if (!highSchool) {
        return null;
    }

    if (classYear) {
        return classYear;
    }

    if (gradYearHs) {
        const gradYear = moment(player.gradYearHs);
        const now = moment();
        const years = gradYear.diff(now, 'days') / 365;

        if (years <= 1.0) {
            return 'SR';
        }

        if (years <= 2.0) {
            return 'JR';
        }

        if (years <= 3.0) {
            return 'SO';
        }

        if (years <= 4.0) {
            return 'FR';
        }

        return 'P-FR';
    }

    return null;
};

export const renderHSInfo = (player) => {
    if (player.highSchool) {
        const year = getPlayerHSGradStatus(player);
        return year ? `${year} - ${player.highSchool}` : player.highSchool;
    }

    return null;
};

export const getPlayerAcceptanceBucket = (acceptance, memberId) => {
    return _.find(acceptance?.buckets, { players: [{ memberId }] });
};

export const getAcceptanceBucket = (acceptance, type) => {
    return _.find(acceptance?.buckets, { type });
};

export const getGenderIdFromPlayerIds = (playerIds, playersDict) => {
    let hasMalePlayers = false;
    let hasFemalePlayers = false;
    playerIds.forEach(id => {
        if (playersDict[id].gender === 'M') {
            hasMalePlayers = true;
        } else if (playersDict[id].gender === 'F') {
            hasFemalePlayers = true;
        }
    });
    if (hasMalePlayers && hasFemalePlayers) {
        return eventGenderTypeIds.ALL;
    }
    if (hasMalePlayers) {
        return eventGenderTypeIds.MALE;
    }
    if (hasFemalePlayers) {
        return eventGenderTypeIds.FEMALE;
    }
    return null;
};

export const getDrawIndex = (draws, drawId) => {
    let drawIndex = 0;
    let childDrawIndex = null;

    draws.forEach((draw, i) => {
        if (draw.id === drawId) {
            drawIndex = i;
        } else if (draw?.childDraws?.length) {
            draw.childDraws.forEach((childDraw, j) => {
                if (childDraw.id === drawId) {
                    drawIndex = i;
                    childDrawIndex = j;
                }
            });
        }
    });

    return [drawIndex, childDrawIndex];
};

const emptyDrawParseResult = () => ({
    membersPlaced: [],
    membersHaveResults: [],
    teamsPlaced: [],
    teamsHaveResults: [],
    membersInDraw: [],
    membersInDrawDict: {},
    membersInWaitlist: [],
    teamsInDraw: [],
    teamsInDrawDict: {},
    teamsInWaitlist: [],
});

const collectSinglesMatchPlacements = (matches) => {
    const membersPlaced = [];
    const membersHaveResults = [];
    matches.forEach(match => {
        if (match?.position1?.memberId && !membersPlaced.includes(match.position1.memberId)) {
            membersPlaced.push(match.position1.memberId);
        }
        if (match?.position2?.memberId && !membersPlaced.includes(match.position2.memberId)) {
            membersPlaced.push(match.position2.memberId);
        }
        if (match?.result && match?.position1?.memberId) {
            membersHaveResults.push(match.position1.memberId);
        }
        if (match?.result && match?.position2?.memberId) {
            membersHaveResults.push(match.position2.memberId);
        }
    });
    return { membersPlaced, membersHaveResults };
};

const collectDoublesMatchPlacements = (matches) => {
    const teamsPlaced = [];
    const teamsHaveResults = [];
    matches.forEach(match => {
        if (match?.position1?.teamId && !teamsPlaced.includes(match.position1.teamId)) {
            teamsPlaced.push(match.position1.teamId);
        }
        if (match?.position2?.teamId && !teamsPlaced.includes(match.position2.teamId)) {
            teamsPlaced.push(match.position2.teamId);
        }
        if (match?.result && match?.position1?.teamId) {
            teamsHaveResults.push(match.position1.teamId);
        }
        if (match?.result && match?.position2?.teamId) {
            teamsHaveResults.push(match.position2.teamId);
        }
    });
    return { teamsPlaced, teamsHaveResults };
};

const collectMembersInDraw = (members) => {
    const membersInDraw = [];
    const membersInDrawDict = {};
    const membersInWaitlist = [];
    members.forEach(member => {
        membersInDraw.push(member.memberId);
        membersInDrawDict[member.memberId] = member;
        if (member.drawPlacementStatusId === drawPlacementStatusTypeIds.WAITLIST) {
            membersInWaitlist.push(member.memberId);
        }
    });
    return { membersInDraw, membersInDrawDict, membersInWaitlist };
};

const collectTeamsInDraw = (teams) => {
    const teamsInDraw = [];
    const teamsInDrawDict = {};
    const teamsInWaitlist = [];
    teams.forEach(team => {
        teamsInDraw.push(team.teamId);
        teamsInDrawDict[team.teamId] = team;
        if (team.drawPlacementStatusId === drawPlacementStatusTypeIds.WAITLIST) {
            teamsInWaitlist.push(team.teamId);
        }
    });
    return { teamsInDraw, teamsInDrawDict, teamsInWaitlist };
};

export const parseDrawResponse = (draw = {}) => {
    if (!draw) {
        return emptyDrawParseResult();
    }

    const allMatches = draw.rounds?.flatMap(round => round.matches) || [];

    const { membersPlaced, membersHaveResults } = isSinglesFormat(draw.formatTypeId)
        ? collectSinglesMatchPlacements(allMatches)
        : { membersPlaced: [], membersHaveResults: [] };

    const { teamsPlaced, teamsHaveResults } = !isSinglesFormat(draw.formatTypeId)
        ? collectDoublesMatchPlacements(allMatches)
        : { teamsPlaced: [], teamsHaveResults: [] };

    const { membersInDraw, membersInDrawDict, membersInWaitlist } =
        collectMembersInDraw(draw.members || []);

    const { teamsInDraw, teamsInDrawDict, teamsInWaitlist } =
        collectTeamsInDraw(draw.teams || []);

    return {
        membersPlaced,
        membersHaveResults,
        teamsPlaced,
        teamsHaveResults,
        membersInDraw,
        membersInDrawDict,
        membersInWaitlist,
        teamsInDraw,
        teamsInDrawDict,
        teamsInWaitlist,
    };
};

export const parsePlayersResponse = (players) => {
    const playersDict = {}; // player dictionary by playerId
    const playersDictByMemberId = {}; // player dictionary by memberId

    if (players?.length) {
        players.forEach(player => {
            playersDict[player.playerId] = player;
            playersDictByMemberId[player.memberId] = player;
        });
    }
    return {
        playersDict,
        playersDictByMemberId,
    };
};

export const parseTeamsResponse = (teams) => {
    const teamsDict = {};

    if (teams?.length) {
        teams.forEach(team => {
            teamsDict[team.id] = team;
        });
    }

    return {
        teamsDict,
    };
};

export const getMembersInTeams = (teamsInDraw = [], teamsDict = {}) => {
    let membersInTeams = [];
    teamsInDraw.forEach(teamId => {
        membersInTeams = [...membersInTeams, ...(teamsDict[teamId]?.members || [])];
    });
    return membersInTeams;
};

export const createDrawPlayerPlacementGroups = (playerLists, players, teams, teamsDict, formatTypeId, eliminationTypeId) => {
    const {
        membersPlaced,
        teamsPlaced,
        membersInDraw,
        membersInWaitlist,
        teamsInDraw,
        teamsInWaitlist,
    } = playerLists;

    const membersInTeams = getMembersInTeams(teamsInDraw, teamsDict);

    let notPlaced = [];
    let placed = [];
    let waitlist = [];
    let notInDraw = [];
    let notInDrawTeams = [];
    let available = [];

    if (isSinglesFormat(formatTypeId)) {
        notPlaced = players.filter(player => membersInDraw?.includes(player.memberId) && !membersPlaced.includes(player.memberId));
        placed = players.filter(player => membersInDraw?.includes(player.memberId) && membersPlaced.includes(player.memberId));
        waitlist = players.filter(player => membersInWaitlist?.includes(player.memberId));
        notInDraw = players.filter(player => !membersInDraw?.includes(player.memberId));
    } else {
        notPlaced = teams.filter(team => teamsInDraw?.includes(team.id) && !teamsPlaced.includes(team.id));
        placed = teams.filter(team => teamsInDraw?.includes(team.id) && teamsPlaced.includes(team.id));
        waitlist = teams.filter(team => teamsInWaitlist?.includes(team.id));
        notInDraw = players.filter(player => !(membersInTeams?.includes(player.memberId) || membersInDraw?.includes(player.memberId)));
        notInDrawTeams = teams.filter(team => !teamsInDraw?.includes(team.id));
    }
    if (eliminationTypeId === drawEliminationTypeIds.AD_HOC || eliminationTypeId === drawEliminationTypeIds.MATCH_PLAY || eliminationTypeId === drawEliminationTypeIds.ROUND_ROBIN) {
        available = players.data?.filter(player => membersInDraw?.includes(player.memberId) || membersInTeams?.includes(player.memberId));
    } else {
        available = players.data?.filter(player => membersInDraw?.includes(player.memberId));
    }
    return {
        notPlaced,
        placed,
        waitlist,
        notInDraw,
        notInDrawTeams,
        membersInTeams,
        available,
    };
};

export const isMemberPlaced = (memberId, rounds = []) => {
    let memberIsPlaced = false;
    // eslint-disable-next-line no-restricted-syntax
    for (const round of rounds) {
        // eslint-disable-next-line no-restricted-syntax
        for (const match of round.matches) {
            if (match?.position1?.memberId === memberId || match?.position2?.memberId === memberId) {
                memberIsPlaced = true;
                break;
            }
        }
        if (memberIsPlaced) {
            break;
        }
    }
    return memberIsPlaced;
};

export const isTeamPlaced = (teamId, rounds = []) => {
    let teamIsPlaced = false;
    // eslint-disable-next-line no-restricted-syntax
    for (const round of rounds) {
        // eslint-disable-next-line no-restricted-syntax
        for (const match of round.matches) {
            if (match?.position1?.teamId === teamId || match?.position2?.teamId === teamId) {
                teamIsPlaced = true;
                break;
            }
        }
        if (teamIsPlaced) {
            break;
        }
    }
    return teamIsPlaced;
};

export const getOtherTeamsOfTeamMembers = (teamId, eventTeams) => {
    const team = eventTeams.find(t => t.id === teamId);
    const otherTeams = [];
    eventTeams.forEach(t => {
        if (t.id !== teamId && (t.members.includes(team.members[0]) || t.members.includes(team.members[1]))) {
            otherTeams.push(t.id);
        }
    });
    return otherTeams;
};

export const doTeamsContainSamePlayers = (teamIds, teamsDict) => {
    const memberIds = [];
    let containsSame = false;
    teamIds.forEach(teamId => {
        teamsDict[teamId].members.forEach(memberId => {
            if (!memberIds.includes(memberId)) {
                memberIds.push(memberId);
            } else {
                containsSame = true;
            }
        });
    });
    return containsSame;
};

export const renderRoundLabel = (round, eliminationTypeId, drawCategoryTypeId = null, nextRound = null) => {
    if (eliminationTypeId === drawEliminationTypeIds.MATCH_PLAY) {
        return 'Matches';
    }
    if (eliminationTypeId && shouldDisplayRoundNumbersV2(eliminationTypeId)) {
        return `Round ${round.number}`;
    }
    if (eliminationTypeId === drawEliminationTypeIds.FEED_IN_CONSOLATION && drawCategoryTypeId === drawCategoryTypeIds.CONSOLATION) {
        const matchCount = round.matches.length;
        let nextRoundMatchCount = 0;
        if (nextRound) {
            nextRoundMatchCount = nextRound.matches.length;
        }
        if (matchCount === matchCounts.FINAL) {
            return `C-Finals`;
        }
        if (matchCount === matchCounts.SEMI_FINAL) {
            return `C-Semifinals`;
        }
        if (matchCount === matchCounts.QUARTER_FINAL && matchCount === nextRoundMatchCount) {
            return `C-Quarterfinals-Q`;
        }
        if (matchCount === matchCounts.QUARTER_FINAL && matchCount > nextRoundMatchCount) {
            return `C-Quarterfinals`;
        }
        if (matchCount > matchCounts.QUARTER_FINAL && matchCount === nextRoundMatchCount) {
            return `C-Round of ${matchCount * 2}-Q`;
        }
        if (matchCount > matchCounts.QUARTER_FINAL && matchCount > nextRoundMatchCount) {
            return `C-Round of ${matchCount * 2}`;
        }
    }
    const matchCount = round.matches.length;
    if (matchCount === 1) {
        return `Finals`;
    }
    if (matchCount === 2) {
        return `Semifinals`;
    }
    if (matchCount === 4) {
        return `Quarterfinals`;
    }
    return `Round of ${matchCount * 2}`;
};

export const populateTeamsForSave = (teamIds, teamsDict) => {
    console.log('populateTeamsForSave', teamIds, teamsDict);
    const populatedTeams = [];
    teamIds.forEach(id => {
        populatedTeams.push({
            id,
            members: teamsDict[id].members,

        });
    });
    return populatedTeams;
};

export const populateMemberArraysForSave = (draw, teamsDict) => {
    const drawMembersUpdate = {
        memberIds: [],
        teams: [],
    };
    const drawWaitlistUpdate = {
        memberIds: [],
        teamIds: [],
    };
    draw.members.forEach(member => {
        drawMembersUpdate.memberIds.push(member.memberId);
        if (member.drawPlacementStatusId === drawPlacementStatusTypeIds.WAITLIST) {
            drawWaitlistUpdate.memberIds.push(member.memberId);
        }
    });
    draw.teams.forEach(team => {
        drawMembersUpdate.teams.push({
            id: team.teamId,
            members: teamsDict[team.teamId].members,
        });
        if (team.drawPlacementStatusId === drawPlacementStatusTypeIds.WAITLIST) {
            drawWaitlistUpdate.teamIds.push(team.teamId);
        }
    });

    return {
        drawMembersUpdate,
        drawWaitlistUpdate,
    };
};

export const populateMatchUpdateList = (draw) => {
    const drawMatchUpdates = {
        matches: [],
    };

    draw.rounds.forEach(round => {
        round.matches.forEach(match => {
            if (match.unsaved) {
                const matchToSave = {
                    id: match.id,
                    position1: null,
                    position2: null,
                    isPrinted: 0,
                };
                if (match.position1?.typeId === drawPositionTypeIds.BYE) {
                    matchToSave.position1 = { isBye: true };
                } else if (match.position1?.memberId) {
                    matchToSave.position1 = { memberId: match.position1.memberId };
                } else if (match.position1?.teamId) {
                    matchToSave.position1 = { teamId: match.position1.teamId };
                }
                if (match.position2?.typeId === drawPositionTypeIds.BYE) {
                    matchToSave.position2 = { isBye: true };
                } else if (match.position2?.memberId) {
                    matchToSave.position2 = { memberId: match.position2.memberId };
                } else if (match.position2?.teamId) {
                    matchToSave.position2 = { teamId: match.position2.teamId };
                }
                if (match.isPrinted === 1) {
                    matchToSave.isPrinted = 1;
                }
                drawMatchUpdates.matches.push(matchToSave);
            }
        });
    });
    return drawMatchUpdates;
};

const getSortValue = ({ player, sortState, organizerRatings, utrType }) => {
    const userIsPowered = true;
    const showDecimals = true;
    switch (sortState.sortParam) {
        case sortParamTypes.SINGLESUTR:
        case 'regStatus': {
            const organizerRating = getOrganizerRating(player.memberId, organizerRatings)?.singlesOrganizerRating;
            const playerSinglesRating = getRatingDetails(setRatingChoice({ player, ratingType: gameTypes.SINGLES, utrType }), userIsPowered, showDecimals);
            return organizerRating || playerSinglesRating.sortRating;
        }
        case sortParamTypes.DOUBLESUTR: {
            const organizerRating = getOrganizerRating(player.memberId, organizerRatings)?.doublesOrganizerRating;
            const playerDoublesRating = getRatingDetails(setRatingChoice({ player, ratingType: gameTypes.DOUBLES, utrType }), userIsPowered, showDecimals);
            return organizerRating || playerDoublesRating.sortRating;
        }
        case sortParamTypes.SINGLESPKB: {
            const organizerRating = getOrganizerRating(player.memberId, organizerRatings)?.singlesOrganizerRating;
            const playerPickleBallRating = getPickleBallRating(player, gameTypes.SINGLES, true);
            return organizerRating || playerPickleBallRating.sortRating;
        }
        case sortParamTypes.DOUBLESPKB: {
            const organizerRating = getOrganizerRating(player.memberId, organizerRatings)?.singlesOrganizerRating;
            const playerPickleBallRating = getPickleBallRating(player, gameTypes.DOUBLES, true);
            return organizerRating || playerPickleBallRating.sortRating;
        }
        case sortParamTypes.PKBRATING: {
            const organizerRating = getOrganizerRating(player.memberId, organizerRatings)?.singlesOrganizerRating;
            const playerPickleBallRating = getPickleBallRating(player);
            return organizerRating || playerPickleBallRating.sortRating;
        }
        default:
            return player[sortState.sortParam];
    }
};

export const sortPlayers = ({ players, sortState, acceptance, allPlayersTabFilter, organizerRatings, utrType }) => {
    players.forEach((player) => {
        switch (sortState.sortParam) {
            case sortParamTypes.NAME:
                player.primarySortValue = player.lastName;
                player.secondarySortValue = player.firstName;
                break;
            case sortParamTypes.SINGLESUTR:
            case sortParamTypes.DOUBLESUTR:
            case sortParamTypes.SINGLESPKB:
            case sortParamTypes.DOUBLESPKB:
            case sortParamTypes.PKBRATING: {
                player.primarySortValue = getSortValue({ player, sortState, organizerRatings, utrType });
                break;
            }
            case sortParamTypes.AGE:
                player.primarySortValue = player.age;
                break;
            case 'regStatus':
                player.primarySortValue = player.regStatusSortKey;
                if (acceptance?.[allPlayersTabFilter]?.loaded) {
                    player.secondarySortValue = 1000 - player.acceptanceBucketPosition; // to order the players in correct order to work with other type lists as well
                } else {
                    player.secondarySortValue = getSortValue({ player, sortState: { sortParam: 'singlesUtr' }, organizerRatings, utrType });
                }
                break;
            default:
                player.primarySortValue = player.regStatusSortKey;
                break;
        }
    });
    return players.sort(comparator(getComparatorArguments(sortState.sortParam, sortState.sortType)));
};

export const sortTeams = ({ teamsInDivision, playersDictByMemberId, sortState, organizerRatings, utrType }) => {
    if (!teamsInDivision) {
        return [];
    }

    teamsInDivision.forEach((team) => {
        const player1 = playersDictByMemberId[team.members[0]];
        const player2 = playersDictByMemberId[team.members[1]];
        switch (sortState.sortParam) {
            case sortParamTypes.NAME:
                team.primarySortValue = `${player1.lastName} ${player2.lastName}`;
                team.secondarySortValue = `${player1.firstName} ${player2.firstName}`;
                break;
            case sortParamTypes.SINGLESUTR:
            case sortParamTypes.DOUBLESUTR:
            case sortParamTypes.SINGLESPKB:
            case sortParamTypes.DOUBLESPKB:
            case sortParamTypes.PKBRATING: {
                const player1Rating = getSortValue({ player: player1, sortState, organizerRatings, utrType });
                const player2Rating = getSortValue({ player: player2, sortState, organizerRatings, utrType });
                team.primarySortValue = (parseFloat(player1Rating) + parseFloat(player2Rating)) / 2;
                break;
            }
            case sortParamTypes.AGE:
                team.primarySortValue = (player1.age || 0 + player2.age || 0) / 2;
                break;
            default:
                team.primarySortValue = '';
                break;
        }
    });
    return teamsInDivision.sort(comparator(getComparatorArguments(sortState.sortParam, sortState.sortType)));
};

const doublesPositionWonAt = (matchPosition, teamsDict, playersDictByMemberId, winnerPlayerId) => {
    if (!matchPosition?.teamId || !teamsDict[matchPosition.teamId]) {
        return false;
    }
    const { members } = teamsDict[matchPosition.teamId];
    const playerIds = members.map(memberId => playersDictByMemberId[memberId].playerId);
    return playerIds.includes(winnerPlayerId);
};

const singlesPositionWonAt = (memberId, playersDictByMemberId, winnerPlayerId) => {
    if (!memberId || !playersDictByMemberId[memberId]) {
        return false;
    }
    const playerId = playersDictByMemberId[memberId]?.playerId;
    return playerId === winnerPlayerId;
};

const getDoublesWhichPositionsWon = (match, teamsDict, playersDictByMemberId) => {
    const winner1 = match.result?.players?.winner1;
    return [
        doublesPositionWonAt(match.position1, teamsDict, playersDictByMemberId, winner1),
        doublesPositionWonAt(match.position2, teamsDict, playersDictByMemberId, winner1),
    ];
};

const getSinglesWhichPositionsWon = (match, playersDictByMemberId) => {
    const winner1 = match.result?.players?.winner1;
    return [
        singlesPositionWonAt(match.position1?.memberId, playersDictByMemberId, winner1),
        singlesPositionWonAt(match.position2?.memberId, playersDictByMemberId, winner1),
    ];
};

export const whichMatchPositionWon = ({ drawFormatTypeId, match, teamsDict, playersDictByMemberId }) => {
    if (OUTCOMES_WITH_NO_WINNER.includes(match.result?.outcome) || match.result?.isWinner === false) {
        return [false, false];
    }

    if (isDoublesFormat(drawFormatTypeId)) {
        return getDoublesWhichPositionsWon(match, teamsDict, playersDictByMemberId);
    }

    if (isSinglesFormat(drawFormatTypeId)) {
        return getSinglesWhichPositionsWon(match, playersDictByMemberId);
    }
    return [false, false];
};

export const canPlayerBeAddedToDraw = (player, division = null) => {
    if (!player) {
        return false;
    }

    const playerRegisteredDivisions = player?.divisions?.filter((pDivision) => {
        return pDivision.registrationStatusTypeId === registrationStatusTypeIds.REGISTERED;
    });

    const isPlayerRegisteredToAtLeastOneDivision = playerRegisteredDivisions?.length > 0;

    if (division && isPlayerRegisteredToAtLeastOneDivision) {
        return playerRegisteredDivisions.filter((pDivision) => {
            return pDivision.id === division.id;
        }).length > 0;
    }

    return isPlayerRegisteredToAtLeastOneDivision;
};

export const populatePrintMatchSheetUpdateList = (draw, matchId = null) => {
    const printMatchSheetUpdates = {
        matches: [],
    };

    if (matchId) {
        const matchToSave = {
            id: matchId,
            isPrinted: 1,
        };
        printMatchSheetUpdates.matches.push(matchToSave);
    } else {
        draw.rounds.forEach(round => {
            round.matches.forEach(match => {
                const matchToSave = {
                    id: match.id,
                    isPrinted: 1,
                };
                printMatchSheetUpdates.matches.push(matchToSave);
            });
        });
    }
    return printMatchSheetUpdates;
};

export const allowDrawCreation = (event) => {
    return event?.eventStateId !== eventStateTypeIds.PRE_REGISTRATION &&
        event?.eventStateId !== eventStateTypeIds.OPEN_REGISTRATION;
};

export const populatePrintMultipleMatchesSheetUpdateList = (matchesIds) => {
    const printMatchSheetUpdates = {
        matches: [],
    };

    matchesIds.forEach(matchId => {
        const matchToSave = {
            id: matchId,
            isPrinted: 1,
        };
        printMatchSheetUpdates.matches.push(matchToSave);
    });
    return printMatchSheetUpdates;
};

export const getDivisionsOfPlayersAndTeamsLinkedToDraw = (
    draw,
    drawsDict,
    playersList = [],
    teamsList = [],
) => {
    const divisionIds = [];
    const drawIdsToCheck = [];

    if (drawsDict[draw.id]?.parentDrawId) {
        drawIdsToCheck.push(drawsDict[draw.id].parentDrawId);
        const parentDraw = drawsDict[drawsDict[draw.id].parentDrawId];
        parentDraw.childDraws.forEach(cd => {
            drawIdsToCheck.push(cd.id);
        });
    } else {
        drawIdsToCheck.push(draw.id);
    }

    const playersInDraw = playersList.filter((player) => {
        return player.draws.some((draw) => drawIdsToCheck.includes(draw.id));
    });
    playersInDraw.forEach(player => {
        if (player?.divisions?.length > 0) {
            player.divisions.forEach(div => {
                if (!divisionIds.includes(div.id)) {
                    divisionIds.push(div.id);
                }
            });
        }
    });

    const teamsInDraw = teamsList.filter((team) => {
        return team.draws.some((draw) => drawIdsToCheck.includes(draw.id));
    });
    teamsInDraw.forEach(team => {
        if (team?.divisions?.length > 0) {
            team.divisions.forEach(div => {
                if (!divisionIds.includes(div.id)) {
                    divisionIds.push(div.id);
                }
            });
        }
    });

    return divisionIds;
};

export const getPlayersOrTeamsCountPlacedInDraw = (
    draw,
    playersList = [],
    teamsList = [],
) => {
    const isGrouped = draw.categoryTypeId === drawCategoryTypeIds.GROUPED;
    const hasChildDraws = Array.isArray(draw.childDraws) && draw.childDraws.length > 0;

    const drawIdsToCheck = isGrouped && hasChildDraws
        ? [draw.id, ...draw.childDraws.filter(cd => cd.categoryTypeId === drawCategoryTypeIds.MAIN).map(cd => cd.id)]
        : [draw.id];

    if (draw.formatTypeId === divisionFormatTypeIds.SINGLES) {
        return playersList.filter(player => player.draws?.some(d => drawIdsToCheck.includes(d.id))).length;
    }

    return teamsList.filter(team => team.draws?.some(d => drawIdsToCheck.includes(d.id))).length;
};
